import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaCheck, FaTimes, FaCrown, FaUser, FaStar, FaFire } from 'react-icons/fa';
import RegisterModal from '../components/RegisterModal';

// Animations - Fixed with css helper
const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const pulse = keyframes`
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.02); }
`;

// Styled Components
const PricingSection = styled.section`
    min-height: 100vh;
    background: linear-gradient(135deg, #0c0c0c 0%, #1a1a1a 50%, #2d2d2d 100%);
    padding: 100px 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background:
                radial-gradient(circle at 20% 50%, rgba(245, 102, 7, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 50%, rgba(245, 102, 7, 0.05) 0%, transparent 50%);
    }
`;

const Container = styled.div`
    max-width: 1200px;
    width: 100%;
    text-align: center;
    position: relative;
    z-index: 2;
`;

const SectionTitle = styled.h2`
    font-size: 3em;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 20px;
    background: linear-gradient(135deg, #ffffff 0%, #f56607 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
        font-size: 2.2em;
    }
`;

const SectionSubtitle = styled.p`
    font-size: 1.3em;
    color: #b0b0b0;
    margin-bottom: 80px;
    line-height: 1.6;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;

    @media (max-width: 768px) {
        font-size: 1.1em;
        margin-bottom: 50px;
    }
`;

const PricingContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    max-width: 1000px;
    margin: 0 auto 80px;

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 30px;
        max-width: 500px;
    }
`;

const PricingCard = styled.div<{ $isPremium?: boolean }>`
    background: rgba(255, 255, 255, 0.05);
    border-radius: 20px;
    padding: 50px 40px;
    border: 1px solid ${props => props.$isPremium ? 'rgba(245, 102, 7, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    animation: ${fadeInUp} 1s ease-out;

    ${props => props.$isPremium && css`
        background: linear-gradient(135deg, rgba(245, 102, 7, 0.1) 0%, rgba(255, 140, 0, 0.05) 100%);
        transform: scale(1.05);

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: linear-gradient(135deg, #f56607 0%, #ff8c00 100%);
        }

        &:hover {
            animation: ${pulse} 2s ease-in-out infinite;
        }
    `}

    &:nth-child(1) { animation-delay: 0.1s; }
    &:nth-child(2) { animation-delay: 0.2s; }

    &:hover {
        transform: ${props => props.$isPremium ? 'scale(1.08)' : 'translateY(-10px)'};
        border-color: ${props => props.$isPremium ? 'rgba(245, 102, 7, 0.5)' : 'rgba(255, 255, 255, 0.2)'};
    }

    @media (max-width: 968px) {
        transform: none !important;

        &:hover {
            transform: translateY(-5px) !important;
        }
    }
`;

const PopularBadge = styled.div`
    position: absolute;
    top: 20px;
    right: 20px;
    background: linear-gradient(135deg, #f56607 0%, #ff8c00 100%);
    color: white;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.8em;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 5px;
`;

const PlanIcon = styled.div<{ $isPremium?: boolean }>`
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: ${props => props.$isPremium
            ? 'linear-gradient(135deg, #f56607 0%, #ff8c00 100%)'
            : 'rgba(255, 255, 255, 0.1)'};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 25px;
    color: ${props => props.$isPremium ? 'white' : '#b0b0b0'};
    font-size: 2em;
`;

const PlanTitle = styled.h3`
    font-size: 2em;
    color: #ffffff;
    margin-bottom: 15px;
    font-weight: 700;
`;

const PlanDescription = styled.p`
    color: #b0b0b0;
    line-height: 1.6;
    margin-bottom: 30px;
    font-size: 1em;
`;

const Price = styled.div<{ $isPremium?: boolean }>`
    font-size: 3em;
    font-weight: 800;
    color: ${props => props.$isPremium ? '#f56607' : '#ffffff'};
    margin-bottom: 10px;

    span {
        font-size: 0.5em;
        color: #b0b0b0;
        font-weight: 400;
    }
`;

const BillingPeriod = styled.div`
    color: #b0b0b0;
    font-size: 0.9em;
    margin-bottom: 40px;
`;

const FeaturesList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0 0 40px 0;
    text-align: left;
`;

const FeatureItem = styled.li<{ $included?: boolean }>`
    color: ${props => props.$included ? '#ffffff' : '#666666'};
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 1em;

    &:last-child {
        margin-bottom: 0;
    }
`;

const IconWrapper = styled.div<{ $included?: boolean }>`
    color: ${props => props.$included ? '#f56607' : '#666666'};
    font-size: 1em;
    flex-shrink: 0;
`;

const CTAButton = styled.button<{ $isPremium?: boolean; $secondary?: boolean }>`
    width: 100%;
    padding: 18px 30px;
    background: ${props => {
        if (props.$isPremium) {
            return 'linear-gradient(135deg, #f56607 0%, #ff8c00 100%)';
        }
        if (props.$secondary) {
            return 'rgba(255, 255, 255, 0.1)';
        }
        return 'rgba(255, 255, 255, 0.05)';
    }};
    color: ${props => props.$isPremium ? 'white' : '#ffffff'};
    border: 1px solid ${props => props.$isPremium ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
    border-radius: 12px;
    font-size: 1.1em;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: ${props => props.$isPremium ? '0 8px 25px rgba(245, 102, 7, 0.3)' : 'none'};

    &:hover {
        transform: translateY(-2px);
        box-shadow: ${props => props.$isPremium
                ? '0 12px 35px rgba(245, 102, 7, 0.4)'
                : '0 8px 20px rgba(255, 255, 255, 0.1)'};
        background: ${props => {
            if (props.$isPremium) {
                return 'linear-gradient(135deg, #ff8c00 0%, #f56607 100%)';
            }
            if (props.$secondary) {
                return 'rgba(255, 255, 255, 0.15)';
            }
            return 'rgba(255, 255, 255, 0.08)';
        }};
    }
`;

const GuaranteeSection = styled.div`
    background: rgba(255, 255, 255, 0.05);
    border-radius: 20px;
    padding: 40px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    animation: ${fadeInUp} 1s ease-out 0.3s both;
    max-width: 800px;
    margin: 0 auto;
`;

const GuaranteeTitle = styled.h4`
    font-size: 1.5em;
    color: #ffffff;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
`;

const GuaranteeText = styled.p`
    color: #b0b0b0;
    line-height: 1.6;
    font-size: 1.1em;
`;

const PricingComparison: React.FC = () => {
    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
    const [registrationType, setRegistrationType] = useState<'free' | 'premium'>('free');

    const freeFeatures = [
        { included: true, text: 'Základný prístup k stránke a aplikácií' },
        { included: true, text: 'Dostupná databáza tipov' },
        { included: true, text: 'Expres správy' },
        { included: false, text: 'Podcasty' },
        { included: false, text: 'Analýzy' },
        { included: false, text: 'Predikcie' },
        { included: false, text: 'Notifikácie' },
        { included: false, text: 'Naše rady počas zápasu' },
        { included: false, text: 'Diskusie' },
    ];


    const premiumFeatures = [
        { included: true, text: 'Neobmedzený prístup k všetkým tipom' },
        { included: true, text: 'Dostupná databáza tipov' },
        { included: true, text: 'Expres správy' },
        { included: true, text: 'Podcasty' },
        { included: true, text: 'Analýzy' },
        { included: true, text: 'Predikcie' },
        { included: true, text: 'Notifikácie' },
        { included: true, text: 'Naše rady počas zápasu' },
        { included: true, text: 'Diskusie' },
    ];

    const handleFreeSignup = () => {
        setRegistrationType('free');
        setIsRegisterModalOpen(true);
    };

    const handlePremiumSignup = () => {
        setRegistrationType('premium');
        setIsRegisterModalOpen(true);
    };

    const handleCloseRegisterModal = () => {
        setIsRegisterModalOpen(false);
    };

    const handleRegisterSuccess = () => {
        console.log(`Registration successful for ${registrationType} plan!`);
        // You can add logic here to handle post-registration flow
        // For premium, you might want to redirect to payment page
        if (registrationType === 'premium') {
            // Navigate to payment page or show upgrade modal
            console.log('Redirecting to premium upgrade...');
        }
    };

    // The one price on sale. It was 19,99 € here and 34,99 € everywhere else — the landing page
    // undercut the checkout by fifteen euros. A yearly price used to sit beside it for a plan that
    // was never wired to anything; monthly-only removed the question.
    const monthlyPrice = '34,99€';

    return (
        <PricingSection>
            <Container>
                <SectionTitle>Vyberte si svoj plán</SectionTitle>
                <SectionSubtitle>
                    Začnite zdarma alebo prejdite na prémiové členstvo pre neobmedzený prístup k exkluzívnym tipom a analýzam.
                </SectionSubtitle>

                <PricingContainer>
                    {/* Free Plan */}
                    <PricingCard>
                        <PlanIcon>
                            <FaUser />
                        </PlanIcon>
                        <PlanTitle>Základný</PlanTitle>
                        <PlanDescription>
                            Perfektný pre začiatočníkov na oboznámenie sa s platformou
                        </PlanDescription>

                        <Price>0€</Price>
                        <BillingPeriod>Navždy zadarmo</BillingPeriod>

                        <FeaturesList>
                            {freeFeatures.map((feature, index) => (
                                <FeatureItem key={index} $included={feature.included}>
                                    <IconWrapper $included={feature.included}>
                                        {feature.included ? <FaCheck /> : <FaTimes />}
                                    </IconWrapper>
                                    {feature.text}
                                </FeatureItem>
                            ))}
                        </FeaturesList>

                        <CTAButton $secondary onClick={handleFreeSignup}>
                            Začať Zdarma
                        </CTAButton>
                    </PricingCard>

                    {/* Premium Plan */}
                    <PricingCard $isPremium>
                        <PopularBadge>
                            <FaStar />
                            Odporúčané
                        </PopularBadge>

                        <PlanIcon $isPremium>
                            <FaCrown />
                        </PlanIcon>
                        <PlanTitle>Prémiový</PlanTitle>
                        <PlanDescription>
                            Kompletný prístup k všetkým prémiovým funkciám a exkluzívnym tipom
                        </PlanDescription>

                        <Price $isPremium>{monthlyPrice}</Price>
                        <BillingPeriod>mesačne • automaticky sa obnovuje • zrušiť kedykoľvek</BillingPeriod>

                        <FeaturesList>
                            {premiumFeatures.map((feature, index) => (
                                <FeatureItem key={index} $included={feature.included}>
                                    <IconWrapper $included={feature.included}>
                                        <FaCheck />
                                    </IconWrapper>
                                    {feature.text}
                                </FeatureItem>
                            ))}
                        </FeaturesList>

                        <CTAButton $isPremium onClick={handlePremiumSignup}>
                            <FaFire />
                            Začať s Prémiom
                        </CTAButton>
                    </PricingCard>
                </PricingContainer>
            </Container>

            {/* Register Modal */}
            <RegisterModal
                isOpen={isRegisterModalOpen}
                onRequestClose={handleCloseRegisterModal}
                onRegister={handleRegisterSuccess}
            />
        </PricingSection>
    );
};

export default PricingComparison;